import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BehaviorSubject, map, of, switchMap } from 'rxjs';

import { BaseFormComponent } from '@app/forms/base-form/base-form';
import { ApiService } from '@app/services/api/api.service';
import { dropdownSeparatorValue } from '@voyagu/portal-design-system';
import { CCNumberValidator, errorsFormTexts } from '@app/forms/formly/validators/validators';
import { getFormatedCurrency, scrollTo } from '@app/utils/utils';
import { Option } from '@app/forms/formly/formly-utils';
import { clientBalanceOption, newCreditCardOption } from '@app/utils/constants';
import { AuthService, User } from '@app/services/auth/auth.service';
import { getTrackingEvent, PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { ErrorService } from '@app/services/error.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';

@Component({
  selector: 'card-form',
  templateUrl: './card-form.component.html',
  styleUrls: ['./card-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardFormComponent
  extends BaseFormComponent<PortalSubmitOptionErrors | null>
  implements OnInit, AfterViewInit
{
  isSplittedPayment$ = new BehaviorSubject(false);

  usCountryCode = '';

  disabledFields: string[] = [];

  user: User | null;

  private clientBalanceAmountField?: FormlyFieldConfig;

  @Input() creditCards: Option[];
  @Input() clientBalanceAmount: number;
  @Input() maxBalanceAmount: number;
  @Input() totalAmount: number;
  @Input() readAndAccept: boolean;

  @Output() valueChange = new EventEmitter();
  @Output() setReadAndAcceptError = new EventEmitter();
  @Output() back = new EventEmitter();

  constructor(
    public dr: DestroyRef,
    protected apiService: ApiService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected errorService: ErrorService,
    private authService: AuthService,
  ) {
    super();
  }

  override ngOnInit() {
    this.user = this.authService.getUser();
  }

  ngAfterViewInit() {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.dr))
      .subscribe(() => this.valueChange.emit());

    this.fields = this.getFields();

    if (this.needsSplit()) {
      this.split();
    } else if (this.maxBalanceAmount >= this.totalAmount && this.totalAmount > 0) {
      queueMicrotask(() => this.form.patchValue({ id: clientBalanceOption.value }));
    }

    this.changeDetectorRef.detectChanges();
  }

  override getFields(): FormlyFieldConfig[] {
    const clientBalanceAmountOpt = this.getClientBalanceOption();

    return [
      {
        fieldGroup: [
          {
            type: 'dropdown',
            className: 'mb-4',
            props: {
              options: of([clientBalanceAmountOpt]),
              disabled: true,
            },
            defaultValue: clientBalanceAmountOpt.value,
          },
          {
            key: 'clientBalanceAmount',
            type: 'currency',
            className: 'mb-4',
            props: {
              label: 'Amount due',
              required: true,
            },
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                this.clientBalanceAmountField = field;

                field.formControl?.valueChanges
                  .pipe(takeUntilDestroyed(this.dr))
                  .subscribe((value) => {
                    const cardAmount = this.totalAmount - value;

                    const formValue = { cardAmount: cardAmount > 0 ? cardAmount : 0 };

                    this.form.patchValue(formValue, { emitEvent: false });
                  });
              },
            },
            expressions: {
              'props.max': () => this.maxBalanceAmount,
              defaultValue: () =>
                this.maxBalanceAmount > this.totalAmount ? this.totalAmount : this.maxBalanceAmount,
            },
          },
          {
            type: 'info-block',
            props: {
              text: `The maximum you can pay at this time is ${this.transformPortalCurrencyPipe(
                this.maxBalanceAmount,
              )}.`,
            },
          },
        ],
        className: 'mb-3',
        wrappers: ['card-wrapper'],
        expressions: {
          hide: this.isSplittedPayment$.pipe(map((vl) => !vl)),
          'props.disabled': this.isSplittedPayment$.pipe(map((vl) => !vl)),
        },
      },
      {
        fieldGroup: [
          {
            key: 'id',
            type: 'dropdown',
            className: 'mb-4',
            props: {
              placeholder: 'Payment method',
              options: this.isSplittedPayment$.pipe(
                switchMap((isSplittedPayment) => {
                  const options = [...(this.creditCards || [])];

                  if (!isSplittedPayment && this.clientBalanceAmount) {
                    options.push(clientBalanceAmountOpt);
                  }

                  return of(options);
                }),
              ),
            },
            defaultValue: newCreditCardOption.value,
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                let prevValue: unknown = field.formControl?.value;

                field.formControl?.valueChanges
                  .pipe(takeUntilDestroyed(this.dr))
                  .subscribe((value) => {
                    if (value === prevValue) return;

                    prevValue = value;

                    if (value === newCreditCardOption.value) {
                      const clientBalanceAmount =
                        this.maxBalanceAmount > this.totalAmount
                          ? this.totalAmount
                          : this.maxBalanceAmount;

                      const formValue = {
                        id: newCreditCardOption.value,
                        clientBalanceAmount,
                        cardAmount: this.totalAmount - clientBalanceAmount,
                      };

                      this.form.reset(formValue);
                    } else if (value === clientBalanceOption.value) {
                      if (this.needsSplit()) {
                        this.split();
                      }
                    } else {
                      const foundCreditCard = this.creditCards?.find((p) => p.value === value);

                      const formValue = { ...(foundCreditCard?.extra || {}) };

                      setTimeout(() => this.form.patchValue(formValue)); // todo timeout
                    }
                  });
              },
            },
            expressions: {
              hide: () => !this.creditCards?.length && !this.clientBalanceAmount,
            },
          },
          {
            key: 'number',
            type: 'cc-number',
            className: 'mb-4',
            props: {
              label: 'Debit/Credit card number',
              required: true,
              placeholder: '0000 0000 0000 0000',
            },
            validators: {
              'cc-number-invalid': CCNumberValidator,
            },
            expressions: {
              hide: this.isNotNewCreditCard.bind(this),
              'props.disabled': this.isNotNewCreditCard.bind(this),
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            key: 'holder',
            type: 'input',
            className: 'mb-4',
            props: {
              label: 'Name on Card',
              required: true,
              attributes: {
                autocomplete: 'cc-name',
              },
            },
            expressions: {
              hide: this.isClientBalance.bind(this),
              'props.disabled': this.isNotNewCreditCard.bind(this),
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            fieldGroup: [
              {
                key: 'expiration',
                type: 'cc-expiration',
                className: 'mr-2 flex-1',
                props: {
                  label: 'Expiration date',
                  required: true,
                  placeholder: 'MM/YY',
                },
                expressions: {
                  hide: this.isClientBalance.bind(this),
                  'props.disabled': this.isNotNewCreditCard.bind(this),
                },
                hooks: {
                  onInit: this.fieldOnInit.bind(this),
                },
                validators: {
                  expirationDate: {
                    expression: this.expirationDateValidator,
                    message: 'Expiration date must be this month or later',
                  },
                },
              },
              {
                key: 'cvv2',
                type: 'cc-cvv',
                className: 'ml-2 flex-1',
                props: {
                  label: 'Security code',
                  required: true,
                },
                expressions: {
                  hide: this.isClientBalance.bind(this),
                  'props.disabled': this.isClientBalance.bind(this),
                },
                hooks: {
                  onInit: this.fieldOnInit.bind(this),
                },
              },
            ],
            fieldGroupClassName: 'd-flex',
          },
          {
            key: 'countryId',
            type: 'dropdown',
            className: 'mb-4 mt-4',
            props: {
              label: 'Country',
              placeholder: 'Select country',
              required: true,
              options: this.apiService.countries().pipe(
                map((options) => {
                  const separatedOptions = ['US', 'GB', 'CA', 'AU'].map((code) => {
                    const index = options.findIndex(({ extra }) => extra?.code === code);
                    return options.splice(index, 1)[0];
                  });

                  this.usCountryCode = separatedOptions[0]?.value;

                  return [...separatedOptions, { value: dropdownSeparatorValue }, ...options];
                }),
              ),
            },
            expressions: {
              hide: this.isNotNewCreditCard.bind(this),
              'props.disabled': this.isNotNewCreditCard.bind(this),
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            key: 'billingAddress1',
            type: 'input',
            className: 'mb-4',
            props: {
              label: 'Address line 1',
              required: true,
            },
            expressions: {
              hide: this.isNotNewCreditCard.bind(this),
              'props.disabled': this.isNotNewCreditCard.bind(this),
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            key: 'billingAddress2',
            type: 'input',
            className: 'mb-4',
            props: {
              label: 'Address line 2 (optional)',
            },
            expressions: {
              hide: this.isNotNewCreditCard.bind(this),
              'props.disabled': this.isNotNewCreditCard.bind(this),
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            fieldGroup: [
              {
                key: 'state',
                type: 'dropdown',
                className: 'mr-2 flex-1',
                props: {
                  label: 'State',
                  placeholder: 'Select state',
                  required: true,
                  options: this.apiService.states(),
                },
                expressions: {
                  hide: () => {
                    const countryId = (this.model as Partial<{ countryId: string }>)['countryId'];

                    return (
                      (countryId && countryId !== this.usCountryCode) || this.isNotNewCreditCard()
                    );
                  },
                  'props.disabled': this.isNotNewCreditCard.bind(this),
                },
                hooks: {
                  onInit: this.fieldOnInit.bind(this),
                },
              },
              {
                key: 'city',
                type: 'input',
                props: {
                  label: 'City',
                  required: true,
                },
                expressions: {
                  className: () => {
                    const countryId = (this.model as Partial<{ countryId: string }>)['countryId'];

                    return countryId
                      ? countryId === this.usCountryCode
                        ? 'ml-2 flex-1'
                        : 'flex-1'
                      : 'ml-2 flex-1';
                  },
                  hide: this.isNotNewCreditCard.bind(this),
                  'props.disabled': this.isNotNewCreditCard.bind(this),
                },
                hooks: {
                  onInit: this.fieldOnInit.bind(this),
                },
              },
            ],
            fieldGroupClassName: 'd-flex',
          },
          {
            key: 'zipCode',
            type: 'input',
            className: 'mt-4',
            props: {
              label: 'ZIP code',
              required: true,
            },
            expressions: {
              hide: this.isNotNewCreditCard.bind(this),
              'props.disabled': this.isNotNewCreditCard.bind(this),
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            key: 'phone',
            type: 'phone',
            props: {
              label: 'Phone number',
              required: true,
            },
            expressions: {
              className: () => {
                return this.isClientBalance() ? 'mb-4' : 'mt-4';
              },
            },
            hooks: {
              onInit: this.fieldOnInit.bind(this),
            },
          },
          {
            type: 'info-block',
            props: {
              text: `An amount of ${this.transformPortalCurrencyPipe(
                this.clientBalanceAmount > this.totalAmount
                  ? this.totalAmount
                  : this.clientBalanceAmount,
              )} will be deducted from your balance.`,
            },
            expressions: {
              hide: this.isNotClientBalance.bind(this),
              'props.disabled': this.isNotClientBalance.bind(this),
            },
          },
          {
            key: 'cardAmount',
            type: 'currency',
            props: {
              label: 'Amount due',
              required: true,
            },
            className: 'mt-4',
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                field.formControl?.valueChanges
                  .pipe(takeUntilDestroyed(this.dr))
                  .subscribe((value) => {
                    const clientBalanceAmount = this.totalAmount - value;

                    const formValue = {
                      clientBalanceAmount: clientBalanceAmount > 0 ? clientBalanceAmount : 0,
                    };

                    this.form.patchValue(formValue, { emitEvent: false });

                    // emitEvent: false emits no control events, so mark the OnPush balance field
                    const balanceField = this.clientBalanceAmountField;
                    balanceField?.options?.detectChanges?.(balanceField);
                  });
              },
            },
            expressions: {
              hide: this.isSplittedPayment$.pipe(map((vl) => !vl)),
              'props.disabled': this.isSplittedPayment$.pipe(map((vl) => !vl)),
              'props.max': () => {
                const clientBalanceAmount = this.form.get('clientBalanceAmount')?.value || 0;

                return this.totalAmount - clientBalanceAmount;
              },
            },
          },
        ],
        wrappers: ['card-wrapper'],
      },
    ];
  }

  expirationDateValidator = (control: AbstractControl<string>): boolean => {
    const value = control.value;
    if (!value) return false;

    const [month, year] = value.split('/').map((v) => parseInt(v, 10));

    if (!month || !year) return false;

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear() % 100;

    return year > currentYear || (year === currentYear && month >= currentMonth);
  };

  isNotNewCreditCard() {
    const idControl = this.form.get('id');
    return Boolean(idControl?.value !== newCreditCardOption.value && this.creditCards?.length);
  }

  isClientBalance() {
    const idControl = this.form.get('id');
    return idControl?.value === clientBalanceOption.value;
  }

  isNotClientBalance() {
    const idControl = this.form.get('id');
    return idControl?.value !== clientBalanceOption.value;
  }

  needsSplit() {
    return (
      this.maxBalanceAmount < this.totalAmount &&
      this.clientBalanceAmount &&
      this.clientBalanceAmount > 0
    );
  }

  split() {
    this.isSplittedPayment$.next(true);

    const clientBalanceAmount =
      this.maxBalanceAmount > this.totalAmount ? this.totalAmount : this.maxBalanceAmount;

    const formValue = {
      id: newCreditCardOption.value,
      clientBalanceAmount,
      cardAmount: this.totalAmount - clientBalanceAmount,
    };

    setTimeout(() => this.form.patchValue(formValue)); // todo timeout

    Tracking.track(PortalTrackingEvents.switchOnSplit);
  }

  merge() {
    this.isSplittedPayment$.next(false);

    Tracking.track(PortalTrackingEvents.switchOffSplit);
  }

  transformPortalCurrencyPipe(n: number) {
    return getFormatedCurrency(n) as string;
  }

  getClientBalanceOption() {
    return {
      value: clientBalanceOption.value,
      label: `${clientBalanceOption.label}: ${this.transformPortalCurrencyPipe(
        this.clientBalanceAmount,
      )}`,
    } as Option;
  }

  override submit() {
    this.form.markAllAsTouched();

    if (this.form.valid && this.readAndAccept) {
      this.disabledFields = Object.keys(this.form.controls).filter(
        (key) => this.form.get(key)?.disabled,
      );
      this.form.disable({ emitEvent: false });
      this.formSubmit.emit(this.model);
    } else if (this.form.invalid) {
      this.form.setErrors({ required: errorsFormTexts.required });
      Tracking.track(PortalTrackingEvents.portalCheckoutPaymentFieldsRequiredError);
      this.scrollToFirstError();
    } else {
      this.setReadAndAcceptError.emit(true);
      scrollTo('#read-and-accept');
    }

    Tracking.track(PortalTrackingEvents.submitCard);
  }

  override setErrors(errors: PortalSubmitOptionErrors) {
    const errorsKeys = Object.keys(errors || {});

    if (errorsKeys?.length) {
      this.form.disabled && this.form.enable();

      errorsKeys?.forEach((errorKey) => {
        switch (errorKey) {
          case 'credit_card':
            Object.keys(errors[errorKey] || {}).forEach((key) => {
              const field = this.form.get(key);

              field?.setErrors({ backend: (errors[errorKey] || {})[key] });
            });

            this.disabledFields.forEach((key) => {
              const field = this.form.get(key);

              field?.disable();
            });

            // todo rewrite BE errors for the phone field
            if (!errors[errorKey]?.phone) {
              this.form.setErrors({ backend: errorsFormTexts.card });
            }

            break;
          case 'error':
            this.form.setErrors({ backend: errorsFormTexts.optionSubmitError });
            break;
          case 'promocode':
            break;
          default:
            this.form.setErrors({ backend: errors[errorKey as keyof PortalSubmitOptionErrors] });
        }
      });

      this.scrollToFirstError();
    }
  }

  fieldOnInit(field: FormlyFieldConfig) {
    field.formControl?.valueChanges.pipe(takeUntilDestroyed(this.dr)).subscribe(() => {
      const hasError = field.formControl?.status === 'INVALID';

      const eventName = getTrackingEvent(field.key as string);

      if (eventName) {
        Tracking.track(`${eventName}_entered`);
      }

      if (eventName && hasError) {
        Tracking.track(`${eventName}_validation_error`);
      }
    });
  }
}

export type PortalSubmitOptionErrors = {
  error?: string;
  passengers?: {
    birthday?: string;
  }[];
  credit_card?: {
    [key: string]: string;
  };
  promocode?: string;
  form?: string;
  client_balance_amount?: string;
  combined_discount?: string;
};
